"use client";

import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { projectQueryKeys } from "./project-query-keys";
import {
  getProjects,
  getProjectsByFieldOfWork,
  searchProjects,
  getProjectById,
  getProjectDetail,
  getProjectSupporters,
  getProjectStatistics,
  getProjectTransactions,
  getRelatedProjects,
} from "@/app/[locale]/projects/_api/project-reads";
import type {
  GetProjectsResponse,
  GetProjectsByFieldOfWorkResponse,
  SearchProjectsResponse,
  GetProjectsRequestParameter,
  GetProjectsByFieldOfWorkRequest,
  SearchProjectsRequest,
  GetProjectByIdRequestParameter,
  GetProjectByIdResponse,
  GetProjectDetailRequestParameter,
  GetProjectDetailResponse,
  GetProjectSupportersRequestParameter,
  GetProjectSupportersResponse,
  GetProjectStatisticsRequestParameter,
  GetProjectStatisticsResponse,
  GetProjectTransactionsRequestParameter,
  GetProjectTransactionsResponse,
  GetRelatedProjectsRequestParameter,
  GetRelatedProjectsResponse,
} from "@/app/[locale]/projects/_api/project-reads";

/**
 * Hook to fetch projects with associations
 */
export function useProjects(
  params: GetProjectsRequestParameter,
  options?: Omit<UseQueryOptions<GetProjectsResponse>, "queryKey" | "queryFn">
) {
  const safeParams = params || {};
  return useQuery({
    queryKey: projectQueryKeys.withAssociations(safeParams),
    queryFn: () => getProjects(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch projects by field of work
 */
export function useProjectsByFieldOfWork(
  params: GetProjectsByFieldOfWorkRequest,
  options?: Omit<UseQueryOptions<GetProjectsByFieldOfWorkResponse>, "queryKey" | "queryFn">
) {
  const safeParams = params || {};
  return useQuery({
    queryKey: projectQueryKeys.byFieldOfWork(safeParams),
    queryFn: () => getProjectsByFieldOfWork(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to search projects
 */
export function useSearchProjects(
  params: SearchProjectsRequest,
  options?: Omit<UseQueryOptions<SearchProjectsResponse>, "queryKey" | "queryFn">
) {
  const safeParams = params || { searchTerm: "", page: 0, size: 10 };
  return useQuery({
    queryKey: projectQueryKeys.search(safeParams),
    queryFn: () => searchProjects(params),
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
    enabled: params?.searchTerm?.trim().length > 0, // Only search if there's a term
    ...options,
  });
}

/**
 * Hook to fetch a single project by ID
 */
export function useProjectById(
  id: GetProjectByIdRequestParameter,
  options?: Omit<UseQueryOptions<GetProjectByIdResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: projectQueryKeys.detail(id),
    queryFn: () => getProjectById(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!id, // Only fetch if ID is provided
    ...options,
  });
}

/**
 * Hook to fetch project with all associations (enhanced detail)
 */
export function useProjectDetail(
  id: GetProjectDetailRequestParameter,
  options?: Omit<UseQueryOptions<GetProjectDetailResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: projectQueryKeys.detail(id),
    queryFn: () => getProjectDetail(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!id, // Only fetch if ID is provided
    ...options,
  });
}

/**
 * Hook to fetch project supporters
 */
export function useProjectSupporters(
  params: GetProjectSupportersRequestParameter,
  options?: Omit<UseQueryOptions<GetProjectSupportersResponse>, "queryKey" | "queryFn">
) {
  const { id, query } = params;
  return useQuery({
    queryKey: projectQueryKeys.supporters(id, query),
    queryFn: () => getProjectSupporters(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!id, // Only fetch if ID is provided
    ...options,
  });
}

/**
 * Hook to fetch project statistics
 */
export function useProjectStatistics(
  id: GetProjectStatisticsRequestParameter,
  options?: Omit<UseQueryOptions<GetProjectStatisticsResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: projectQueryKeys.statistics(id),
    queryFn: () => getProjectStatistics(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!id, // Only fetch if ID is provided
    ...options,
  });
}

/**
 * Hook to fetch project transactions
 */
export function useProjectTransactions(
  params: GetProjectTransactionsRequestParameter,
  options?: Omit<UseQueryOptions<GetProjectTransactionsResponse>, "queryKey" | "queryFn">
) {
  const { id, query } = params;
  return useQuery({
    queryKey: projectQueryKeys.transactions(id, query),
    queryFn: () => getProjectTransactions(params),
    staleTime: 1 * 60 * 1000, // 1 minute for transaction data
    enabled: !!id, // Only fetch if ID is provided
    ...options,
  });
}

/**
 * Hook to fetch related projects from the same charity
 */
export function useRelatedProjects(
  id: GetRelatedProjectsRequestParameter,
  options?: Omit<UseQueryOptions<GetRelatedProjectsResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: projectQueryKeys.related(id),
    queryFn: () => getRelatedProjects(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!id,
    ...options,
  });
}

/**
 * Generic hook for dynamic project fetching based on filters
 * This combines all the different project fetching patterns into one
 */
export function useProjectsGeneric(
  filters: {
    fieldOfWorkId?: string | null;
    popular?: boolean;
    searchTerm?: string;
    page: number;
    size: number;
  },
  options?: Omit<
    UseQueryOptions<
      GetProjectsResponse | GetProjectsByFieldOfWorkResponse | SearchProjectsResponse
    >,
    "queryKey" | "queryFn"
  >
) {
  const { fieldOfWorkId, popular, searchTerm, page, size } = filters;

  // Determine which query to use based on filters
  const getQueryConfig = () => {
    // Search takes priority.
    // fieldOfWorkId is included in the queryKey so cache is per-category,
    // even though the search API doesn't accept it as a filter.
    // Actual filtering by fieldOfWork is done client-side in ProjectBrowser.
    if (searchTerm && searchTerm.trim()) {
      return {
        queryKey: projectQueryKeys.search({ searchTerm, fieldOfWorkId: fieldOfWorkId ?? null, page, size }),
        queryFn: () => searchProjects({ searchTerm, page, size }),
      };
    }

    // Field of work filter (legacy path, kept for backwards compat)
    if (fieldOfWorkId && !popular) {
      return {
        queryKey: projectQueryKeys.byFieldOfWork({ fieldOfWorkId, page, size }),
        queryFn: () => getProjectsByFieldOfWork({ fieldOfWorkId, page, size }),
      };
    }

    // Default: all projects with optional new filter params
    const params = {
      page,
      size,
      sort: ["name,asc"],
      ...(fieldOfWorkId ? { fieldOfWorkId } : {}),
      ...(popular !== undefined ? { popular } : {}),
    };
    return {
      queryKey: projectQueryKeys.withAssociations(params),
      queryFn: () => getProjects(params),
    };
  };

  const queryConfig = getQueryConfig();

  return useQuery({
    ...queryConfig,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}